import type { DrawContext } from './draw';
import { outerR } from './draw';
import type { LabelSpace } from './labels';
import type { MapNode, ShownNode } from './nodes';
import { namedBy } from './nodes';
import type { View } from './project';
import { toScreen } from './project';

/*
 * WHO IS STANDING HERE — a ring of little pills naming the readers at a name.
 *
 * Ported from `reference/universe-v8/engine.js` — `drawFrame` lines 1920–1974.
 *
 * THE LEDGER CALLED THESE "passage labels" AND THEY ARE NOT. They carry reader handles, not
 * chapter and verse: `readersAt` walks their `HANDLES` list, which is a mock of the presence plane.
 * Ours will come from the real one. The name in `V8-PORT.md` is corrected to match.
 *
 * THIS IS WHERE THE MAP STOPS BEING A DIAGRAM. Everything else says what the texts say; this says
 * who else is here, by name, right now — and it is the only layer whose content is other people.
 * That is why it is the last thing to appear as the reader comes in and the first to be crowded
 * out: it is worth the space only when there is space to spare.
 *
 * IT ONLY NAMES READERS AT NAMES THE READER HAS WALKED. A chip over an unopened figure would be
 * telling somebody about strangers in a room they have not entered, and the map has a rule about
 * that everywhere else too.
 *
 * ITS OWN SPACE, NOT THE LABELS'. Theirs keeps a separate `placed` list rather than consulting the
 * `tboxes` the names were placed into, so A CHIP MAY LAND ON A NODE LABEL. It is theirs and it is
 * kept; the alternative is a chip layer that mostly cannot place anything, since by the zoom these
 * appear at the names have already taken the good space. What it does avoid is the DISCS — every
 * shown name within 190 pixels, its ring included — because a chip over a figure hides the thing
 * the chip is about.
 */

/** Their turn, in radians, exactly as written in the reference. */
const TAU = 6.2832;

/**
 * THE ZOOM THE CHIPS APPEAR AT — an argument, because a zoom is pixels per world unit.
 *
 * Their 1.05 is the twenty-ninth absolute zoom lifted out of this engine, and the highest of them:
 * nothing else in the map waits this long to appear.
 */
export interface ChipZooms {
  from: number;
}

/** What the reader has open and where they have been. */
export interface ChipFocus {
  /** The index of the figure the reader opened, or null — it is served first and served wider. */
  focus?: number | null;
  /** Whether a name has been walked. Only walked names get chips at all. */
  walked: (id: number) => boolean;
}

/**
 * WHO IS AT A NAME, in the order they should be shown, at most `cap` of them.
 *
 * The most obviously OURS thing in the whole port: theirs indexes a hard-coded list of eighteen
 * invented handles by `id*7 + i*3`. A caller wires this to the presence plane.
 */
export type ReadersAt = (node: MapNode, cap: number) => readonly string[];

/** How many names may carry chips in one frame. A budget, so mechanism. */
const NODE_CAP = 7;

/** How far past the screen a name is still considered. Wider than tall, as theirs is. */
const CULL_X = 60;
const CULL_Y = 40;

/** How far a figure's disc may be drawn, in screen pixels. */
const DISC_MIN = 4;
const DISC_MAX = 46;

/** How far around a name other discs are looked for, and how much air each of them wants. */
const NEAR_WINDOW = 190;
const DISC_CLEAR = 4;

/** How far out the ring of chips sits, past the figure's own outermost mark. */
const RING_OUT = 13;
const RING_OUT_FOCUS = 18;

/** How many readers are named — more at the open figure, because that is what was asked about. */
const READERS = 3;
const READERS_FOCUS = 6;

/** How fast the ring turns, and by how much each name's ring is offset from its neighbours'. */
const SPIN_RATE = 0.06;
const SPIN_RATE_FOCUS = 0.1;
const SPIN_PER_ID = 0.7;

/** The chip: the air around its text, its height, and how flat the ring it sits on is. */
const CHIP_PAD = 24;
const CHIP_H = 18;
const RING_FLATTEN = 0.82;

/** How many places a chip tries before it gives up, and how far apart the tries are. */
const TRIES = 7;
const TRY_STEP = 0.34;

/** How close to the edge of the map a chip may sit, and the air it reserves once placed. */
const EDGE = 6;
const RESERVE = 3;

/** The look: the plate, its edge, the dot, and the ink. */
const CHIP_ALPHA = 0.8;
const CHIP_FILL = 'rgba(6,8,16,.9)';
const CHIP_RADIUS = 9;
const CHIP_EDGE = 'rgba(99,224,232,.26)';
const CHIP_EDGE_FOCUS = 'rgba(99,224,232,.5)';
const CHIP_EDGE_WIDTH = 1;
const DOT_COLOUR = '#63e0e8';
const DOT_R = 2.1;
const DOT_IN = 9;
const INK = '#a9dfe4';
const INK_FOCUS = '#d6f6f9';
const TEXT_IN = 15;
/** Their half-pixel nudge, which is what puts the text on the pixel grid rather than across it. */
const TEXT_DROP = 0.5;

/** The font the handles are set in. Look. */
const CHIP_FONT = '500 11px "Space Grotesk", sans-serif';

/** A disc a chip must keep clear of: where it is and how far its mark reaches. */
interface Disc {
  id: number;
  sx: number;
  sy: number;
  reach: number;
}

/**
 * Draw the chips. Returns how many NAMES carried at least one — theirs counts names, not chips.
 *
 * `space` is a LabelSpace of the chips' own, holding the same chrome as the frame's but its own
 * reservations, which is exactly the separation theirs has between `placed` and `tboxes`.
 */
export function drawReaderChips(
  ctx: DrawContext,
  view: View,
  nodes: readonly MapNode[],
  shown: readonly ShownNode[],
  palette: Readonly<Record<string, string>>,
  readersAt: ReadersAt,
  zooms: ChipZooms,
  space: LabelSpace,
  focus: ChipFocus,
  seconds: number,
): number {
  const z = view.cam.z;
  if (z <= zooms.from) return 0;
  const W = view.view.width;
  const H = view.view.height;
  const fid = focus.focus ?? null;
  ctx.font = CHIP_FONT;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';

  /* Only names somebody is standing at, and only ones the reader has been to themselves. */
  const list = nodes.filter(n => n.pop > 0 && focus.walked(n.id));
  /* The open figure first. A stable sort, so everything else keeps the roster's own order. */
  if (fid !== null) list.sort((a, b) => (b.id === fid ? 1 : 0) - (a.id === fid ? 1 : 0));

  let drew = 0;
  for (let i = 0; i < list.length && drew < NODE_CAP; i++) {
    const n = list[i];
    if (n === undefined) continue;
    const s = toScreen(view, n.x, n.y);
    if (s.x < -CULL_X || s.x > W + CULL_X || s.y < -CULL_Y || s.y > H + CULL_Y) continue;
    const r = Math.max(DISC_MIN, Math.min(DISC_MAX, n.r * z));
    const isFocus = n.id === fid;
    /* Only the discs near enough to matter, so this is a short list rather than the whole field. */
    const near: Disc[] = [];
    for (const q of shown) {
      if (Math.abs(q.sx - s.x) >= NEAR_WINDOW || Math.abs(q.sy - s.y) >= NEAR_WINDOW) continue;
      near.push({ id: q.node.id, sx: q.sx, sy: q.sy, reach: (q.hit || q.r) + DISC_CLEAR });
    }
    const rad = outerR(r, namedBy(n, palette).length, n.pop) + (isFocus ? RING_OUT_FOCUS : RING_OUT);
    const handles = readersAt(n, isFocus ? READERS_FOCUS : READERS);
    /* Each name's ring turns at its own phase, so a screen of them never marches in step. */
    const spin = seconds * (isFocus ? SPIN_RATE_FOCUS : SPIN_RATE) + n.id * SPIN_PER_ID;
    let any = false;

    handles.forEach((handle, j) => {
      const w = ctx.measureText(handle).width + CHIP_PAD;
      const base = spin + (j / handles.length) * TAU;
      for (let attempt = 0; attempt < TRIES; attempt++) {
        /* Its own place first, then alternately clockwise and anticlockwise, widening each pair. */
        const ang = base + (attempt === 0
          ? 0
          : (attempt % 2 === 1 ? 1 : -1) * Math.ceil(attempt / 2) * TRY_STEP);
        const px = s.x + Math.cos(ang) * rad;
        const py = s.y + Math.sin(ang) * rad * RING_FLATTEN;
        const x0 = px - w / 2;
        const y0 = py - CHIP_H / 2;
        const x1 = x0 + w;
        const y1 = y0 + CHIP_H;
        if (x0 < EDGE || x1 > W - EDGE || y0 < EDGE || y1 > H - EDGE) continue;
        if (space.blocked({ x0, y0, x1, y1 })) continue;
        /*
         * The disc test comes before the claim, where theirs runs it after — the same picture,
         * because all three of their tests are pure and the reservation happens only once all
         * three have passed. Ordered this way here so nothing is reserved on a chip's behalf that
         * is then refused.
         */
        if (overDisc(near, n.id, x0, y0, x1, y1)) continue;
        if (!space.claim({ x0, y0, x1, y1 }, {
          x0: x0 - RESERVE, y0: y0 - RESERVE, x1: x1 + RESERVE, y1: y1 + RESERVE,
        })) continue;

        ctx.globalAlpha = isFocus ? 1 : CHIP_ALPHA;
        ctx.fillStyle = CHIP_FILL;
        ctx.beginPath();
        /* Rounded where the canvas has it, square where it does not. Theirs tests for it and so
           does this: a square corner is a worse shape, not a broken one. */
        if (ctx.roundRect) ctx.roundRect(x0, y0, w, CHIP_H, CHIP_RADIUS);
        else ctx.rect(x0, y0, w, CHIP_H);
        ctx.fill();
        ctx.strokeStyle = isFocus ? CHIP_EDGE_FOCUS : CHIP_EDGE;
        ctx.lineWidth = CHIP_EDGE_WIDTH;
        ctx.stroke();
        ctx.fillStyle = DOT_COLOUR;
        ctx.beginPath();
        ctx.arc(x0 + DOT_IN, py, DOT_R, 0, TAU);
        ctx.fill();
        ctx.fillStyle = isFocus ? INK_FOCUS : INK;
        ctx.fillText(handle, x0 + TEXT_IN, py + TEXT_DROP);
        ctx.globalAlpha = 1;
        any = true;
        break;
      }
    });
    if (any) drew += 1;
  }
  return drew;
}

/** Whether a box lies across any nearby disc but the one it belongs to. */
function overDisc(near: readonly Disc[], own: number, x0: number, y0: number, x1: number, y1: number): boolean {
  for (const q of near) {
    if (q.id === own) continue;
    if (x0 < q.sx + q.reach && x1 > q.sx - q.reach && y0 < q.sy + q.reach && y1 > q.sy - q.reach) return true;
  }
  return false;
}
